import React, { useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { useGetAllBookings } from "../../api/booking";
import { useNavigate } from "react-router-dom";

const PendingConfirmation = () => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [bookingTypeFilter, setBookingTypeFilter] = useState("all");
  const navigate = useNavigate();

  const {
    data: bookingsData,
    isLoading,
    error
  } = useGetAllBookings(statusFilter, bookingTypeFilter);

  const bookings = bookingsData?.data || [];

  return (
    <React.Fragment>
      <div
        className="page-content"
        style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
      >
        <div className="container-fluid">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between py-3">
                <h4
                  className="mb-0"
                  style={{
                    color: "#495057",
                    fontWeight: "bold",
                    fontSize: "24px",
                    letterSpacing: "0.5px"
                  }}
                >
                  BOOKING CONFIRMATIONS
                </h4>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="row mb-4">
            <div className="col-12">
              <Card className="shadow-sm">
                <CardBody className="p-3">
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="statusFilter" className="fw-bold">
                          Status
                        </Label>
                        <Input
                          type="select"
                          id="statusFilter"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          style={{ borderRadius: "8px" }}
                        >
                          <option value="all">All</option>
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="bookingTypeFilter" className="fw-bold">
                          Booking Type
                        </Label>
                        <Input
                          type="select"
                          id="bookingTypeFilter"
                          value={bookingTypeFilter}
                          onChange={(e) => setBookingTypeFilter(e.target.value)}
                          style={{ borderRadius: "8px" }}
                        >
                          <option value="all">All</option>
                          <option value="urgent">Urgent</option>
                          <option value="not_urgent">Not Urgent</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <div className="text-muted">
                        {isLoading
                          ? "Loading..."
                          : `${bookings.length} bookings found`}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Booking Cards */}
          <Row className="mt-3 g-2">
            {isLoading ? (
              <Col xs={12} className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading bookings...</p>
              </Col>
            ) : error ? (
              <Col xs={12} className="text-center py-5">
                <div className="alert alert-danger">
                  Error loading bookings: {error.message}
                </div>
              </Col>
            ) : bookings.length === 0 ? (
              <Col xs={12} className="text-center py-5">
                <div className="text-muted">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>No bookings found with the selected filters.</p>
                </div>
              </Col>
            ) : (
              bookings.map((booking) => {
                const firstShift = booking.shiftPattern?.[0];
                const firstDate = firstShift?.dates?.[0];
                const submissionDate = new Date(booking.createdAt);
                const startDate = firstDate ? new Date(firstDate.date) : null;

                return (
                  <Col lg={4} md={6} sm={12} key={booking.id} className="mb-3">
                    <Card
                      className="shadow-sm h-100"
                      style={{
                        borderRadius: "16px",
                        border: "3px solid #adb5bd",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                      }}
                    >
                      <CardBody className="p-4 d-flex flex-column">
                        {/* Client Name */}
                        <h5
                          className="mb-3"
                          style={{
                            color: "#495057",
                            fontWeight: "bold",
                            fontSize: "18px",
                            lineHeight: "1.2"
                          }}
                        >
                          {booking.createdBy?.fullName || "Unknown Client"}
                        </h5>

                        {/* Status Badge */}
                        <div className="mb-3">
                          <span
                            className={`badge ${
                              booking.status === "pending"
                                ? "bg-warning"
                                : booking.status === "approved"
                                ? "bg-success"
                                : booking.status === "rejected"
                                ? "bg-danger"
                                : "bg-secondary"
                            }`}
                            style={{
                              fontSize: "12px",
                              textTransform: "uppercase"
                            }}
                          >
                            {booking.status}
                          </span>
                          <span
                            className={`badge ms-2 ${
                              booking.bookingType === "urgent"
                                ? "bg-danger"
                                : "bg-info"
                            }`}
                            style={{
                              fontSize: "12px",
                              textTransform: "uppercase"
                            }}
                          >
                            {booking.bookingType === "urgent"
                              ? "Urgent"
                              : "Not Urgent"}
                          </span>
                        </div>

                        {/* Location */}
                        <div className="mb-3">
                          <small
                            className="text-muted d-block mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#6c757d"
                            }}
                          >
                            Location
                          </small>
                          <span
                            style={{
                              color: "#495057",
                              fontSize: "14px",
                              fontWeight: "400"
                            }}
                          >
                            {booking.location?.siteName || "N/A"}
                          </span>
                        </div>

                        {/* Submission Date and Time */}
                        <div className="mb-3">
                          <Row>
                            <Col xs={6}>
                              <small
                                className="text-muted d-block mb-1"
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  color: "#6c757d"
                                }}
                              >
                                Submission Date
                              </small>
                              <span
                                style={{
                                  color: "#495057",
                                  fontSize: "14px",
                                  fontWeight: "400"
                                }}
                              >
                                {submissionDate.toLocaleDateString()}
                              </span>
                            </Col>
                            <Col xs={6}>
                              <small
                                className="text-muted d-block mb-1"
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  color: "#6c757d"
                                }}
                              >
                                Time
                              </small>
                              <span
                                style={{
                                  color: "#495057",
                                  fontSize: "14px",
                                  fontWeight: "400"
                                }}
                              >
                                {submissionDate.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit"
                                })}
                              </span>
                            </Col>
                          </Row>
                        </div>

                        {/* Booking Start Date and Time */}
                        {startDate && (
                          <div className="mb-3">
                            <Row>
                              <Col xs={6}>
                                <small
                                  className="text-muted d-block mb-1"
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    color: "#6c757d"
                                  }}
                                >
                                  Start Date
                                </small>
                                <span
                                  style={{
                                    color: "#495057",
                                    fontSize: "14px",
                                    fontWeight: "400"
                                  }}
                                >
                                  {startDate.toLocaleDateString()}
                                </span>
                              </Col>
                              <Col xs={6}>
                                <small
                                  className="text-muted d-block mb-1"
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    color: "#6c757d"
                                  }}
                                >
                                  Time
                                </small>
                                <span
                                  style={{
                                    color: "#495057",
                                    fontSize: "14px",
                                    fontWeight: "400"
                                  }}
                                >
                                  {firstShift?.startTime || "N/A"}
                                </span>
                              </Col>
                            </Row>
                          </div>
                        )}

                        {/* Separator Line */}
                        <hr
                          style={{
                            border: "none",
                            borderTop: "2px solid #adb5bd",
                            margin: "16px -16px 16px -16px",
                            width: "calc(100% + 32px)"
                          }}
                        />

                        {/* Review Booking Button */}
                        <div className="text-center mt-auto">
                          <Button
                            color="link"
                            className="p-0 border-0"
                            style={{
                              color: "#20c997",
                              textDecoration: "none",
                              fontSize: "15px",
                              fontWeight: "500",
                              backgroundColor: "transparent",
                              border: "none",
                              outline: "none"
                            }}
                            onClick={() =>
                              navigate(`/booking-review/${booking.slug}`)
                            }
                            onMouseEnter={(e) => {
                              e.target.style.color = "#17a085";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = "#20c997";
                            }}
                          >
                            Review Booking
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default PendingConfirmation;
